/**
 * Auth Service
 *
 * Handles all authentication-related API operations including:
 * - User login/logout
 * - Token refresh
 * - User registration
 * - Email verification
 * - Password reset
 */

import { baseClient } from './base-client';
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  RegisterRequest,
  RegisterResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  ResendVerificationResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ConfirmPasswordResetRequest,
  ConfirmPasswordResetResponse,
  LogoutResponse,
} from './types';

/**
 * JWT Token Payload structure
 */
interface JwtPayload {
  sub?: string;
  userId?: string;
  tenantId: string;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

class AuthService {
  private readonly basePath = '/auth';

  /**
   * Authenticate user with email and password
   */
  async login(request: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await baseClient.post<LoginResponse>(`${this.basePath}/login`, request);

    // Store tokens if login successful
    if (response.success && response.data) {
      baseClient.setAccessToken(response.data.accessToken);
      baseClient.setRefreshToken(response.data.refreshToken);
      baseClient.setTenantId(response.data.user.tenantId);
    }

    return response;
  }

  /**
   * Logout current user
   */
  async logout(): Promise<ApiResponse<LogoutResponse>> {
    const response = await baseClient.post<LogoutResponse>(`${this.basePath}/logout`);

    // Clear stored tokens
    if (response.success) {
      localStorage.removeItem('quiz_commerce_access_token');
      localStorage.removeItem('quiz_commerce_refresh_token');
    }

    return response;
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(request: RefreshTokenRequest): Promise<ApiResponse<RefreshTokenResponse>> {
    const response = await baseClient.post<RefreshTokenResponse>(
      `${this.basePath}/refresh`,
      request
    );

    // Update stored access token if refresh successful
    if (response.success && response.data) {
      baseClient.setAccessToken(response.data.accessToken);
    }

    return response;
  }

  /**
   * Register a new user
   */
  async register(request: RegisterRequest): Promise<ApiResponse<RegisterResponse>> {
    const response = await baseClient.post<RegisterResponse>(`${this.basePath}/register`, request);

    // Store token if registration successful
    if (response.success && response.data) {
      baseClient.setAccessToken(response.data.accessToken);
      baseClient.setTenantId(request.tenantId);
    }

    return response;
  }

  /**
   * Verify user email with verification token
   */
  async verifyEmail(request: VerifyEmailRequest): Promise<ApiResponse<VerifyEmailResponse>> {
    return baseClient.post<VerifyEmailResponse>(`${this.basePath}/verify`, request);
  }

  /**
   * Resend the email verification email for the authenticated user
   */
  async resendVerificationEmail(): Promise<ApiResponse<ResendVerificationResponse>> {
    return baseClient.post<ResendVerificationResponse>(
      `${this.basePath}/resend-verification`,
      {},
    );
  }

  /**
   * Initiate password reset process
   */
  async resetPassword(request: ResetPasswordRequest): Promise<ApiResponse<ResetPasswordResponse>> {
    return baseClient.post<ResetPasswordResponse>(`${this.basePath}/reset-password`, request);
  }

  /**
   * Complete password reset with token and new password
   */
  async confirmPasswordReset(
    request: ConfirmPasswordResetRequest
  ): Promise<ApiResponse<ConfirmPasswordResetResponse>> {
    return baseClient.post<ConfirmPasswordResetResponse>(
      `${this.basePath}/confirm-reset`,
      request
    );
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('quiz_commerce_access_token');
    return !!token;
  }

  /**
   * Get current user info from stored token (basic decode)
   */
  getCurrentUser(): { userId: string; tenantId: string } | null {
    const token = localStorage.getItem('quiz_commerce_access_token');
    if (!token) return null;

    try {
      // Basic JWT decode (payload is between first and second dot)
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload)) as JwtPayload;
      return {
        userId: decoded.sub || decoded.userId || '',
        tenantId: decoded.tenantId,
      };
    } catch (error) {
      return null;
    }
  }
}

/**
 * Export singleton instance
 */
export const authService = new AuthService();
